import { memo } from 'react'
import styled from 'styled-components'
import { m as motion } from 'framer-motion'
import { media, dash, dashOut } from './globalStyles'

export const AppNavigation = memo(styled(motion.header)`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 6.25rem;
  height: 100%;
  padding: 2.1875rem 0;
  right: 0;
  top: 0;
  z-index: 20;
  ${media.lessThan('miniTablet')`
    width: 5rem;
  `};
  ${media.lessThan('phone')`
    width: 4.5rem;
    padding: 1.25rem 0;
  `};
  &.hide-header {
    transition-duration: 500ms;
    opacity: 0 !important;
    visibility: hidden;
    z-index: -1;
  }
`)

export const MainNavigation = memo(styled(motion.aside)`
  position: fixed;
  width: 100vw;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);
  display: grid;
  place-items: center;
  right: 0;
  top: 0;
  z-index: 10;
  ${media.lessThan('phone')`
    display: block;
    padding-top: 8rem;
    padding-left: 4rem;
  `}
  ${media.lessThan('miniPhone')`
    padding-top: 6rem;
    padding-left: 2rem;
  `}
  nav {
    ul {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: 3.25rem 3.125rem;
      ${media.lessThan('phone')`
        grid-template-columns: 1fr;
        grid-gap: 1rem;
      `};
      li {
        button {
          position: relative;
          font-size: 2.25rem;
          line-height: 2.625rem;
          font-weight: 700;
          text-transform: uppercase;
          color: white;
          padding-bottom: 0.875rem;
          transition: all 300ms ease-out;
          transition-property: color;
          will-change: color;
          ${media.lessThan('phone')`
            font-size: 1.875rem;
            line-height: 2.375rem;
            padding-bottom: 0.5rem;
          `}
          ${media.lessThan('miniPhone')`
            font-size: 1.625rem;
            line-height: 2rem;
          `}
          &:after {
            content: '';
            position: absolute;
            width: 100%;
            height: 1px;
            left: 0;
            bottom: 0;
            background: white;
            transform: scaleX(0);
            ${media.greaterThan('phone')`
              transition: transform 300ms ease-out;
              transform-origin: right center;
            `};
          }
          &.active {
            color: ${(props) => props.theme.darkBlue};
            &:after {
              transform: scaleX(1);
              transform-origin: left center;
            }
          }
          ${media.greaterThan('phone')`
            &:hover {
              &:after {
                transform: scaleX(1);
                transform-origin: left center;
              }
            }
          `}
        }
        &:nth-child(odd) {
          text-align: right;
          ${media.lessThan('phone')`
            text-align: left;
          `};
        }
      }
    }
  }
  .language {
    position: absolute;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: flex-end;
    right: 5rem;
    bottom: 3.5rem;
    font-size: 1.625rem;
    line-height: 1.875rem;
    font-weight: 700;
    transition: color 300ms ease-out;
    color: white;
    z-index: 1;
    ${media.lessThan('phone')`
      right: auto;
      left: 4rem;
      bottom: 6rem;
    `};
    ${media.lessThan('miniPhone')`
      left: 1.875rem;
    `};
    span {
      display: block;
      align-self: center;
      width: 1.5625rem;
      margin-top: 0.3125rem;
      margin-right: 0.5rem;
      svg {
        width: 100%;
        fill: white;
        transition: fill 300ms ease-out;
      }
    }
    &:hover {
      color: ${(props) => props.theme.darkBlue};
      span {
        svg {
          fill: ${(props) => props.theme.darkBlue};
        }
      }
    }
  }
`)

export const ToggleMenuButton = memo(styled.button`
  position: relative;
  width: 3rem;
  height: 3rem;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  z-index: 11;
  div {
    position: relative;
    width: 1.625rem;
    height: 1.625rem;
  }
  & > svg {
    position: absolute;
    width: 110%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    ${media.lessThan('phone')`
      display: none;
    `};
    path {
      stroke-dasharray: 200;
      stroke-dashoffset: 200;
    }
  }
  span {
    position: absolute;
    display: block;
    left: 0;
    width: 100%;
    height: 0.1875rem;
    background-color: black;
    transition: all 300ms linear;
    transform-origin: right center;
    transition-property: transform, opacity, background;
    will-change: transform, opacity;
    &:first-child {
      top: 0;
      transform: none;
    }
    &:nth-child(2) {
      top: calc(50% - 0.0938rem);
      transform: none;
    }
    &:last-child {
      bottom: 0;
      transform: none;
    }
  }
  ${media.greaterThan('phone')`
    &:not(.active):hover {
      span {
        &:first-child {
          top: 0;
          transform: scaleX(0.8);
        }
        &:nth-child(2) {
          top: calc(50% - 0.0938rem);
          transform: scaleX(0.5);
        }
        &:last-child() {
          bottom: 0;
          transform: scaleX(0.6);
        }
      }
    }
  `}
  &.active {
    svg {
      path {
        animation: ${dash} 1s linear forwards;
        animation-delay: 300ms;
      }
    }
    span {
      background: white !important;
      &:first-child {
        top: 1px;
        left: -2px;
        transform: rotate(-45deg) scaleX(1.2);
      }
      &:nth-child(2) {
        opacity: 0;
      }
      &:last-child {
        left: -2px;
        transform: rotate(45deg) scaleX(1.2);
      }
    }
    ${media.greaterThan('phone')`
      &:hover {
        span {
          background: ${(props) => props.theme.darkBlue} !important;
          transition-delay: 450ms;
        }
        svg {
          path {
            animation: ${dashOut} 0.5s linear forwards;
            animation-delay: 0s;
          }
        }
      }
    `}
  }
  ${media.greaterThan('phone')`
    .quienes-somos &, .contacto & {
      span {
        background: white;
        ${media.lessThan('tablet')`
          .quienes-somos & {
            background: black;
          }
        `};
      }
    }
  `}
  ${media.lessThan('phone')`
    .contacto & {
      span {
        background: white;
        ${media.lessThan('tablet')`
          .quienes-somos & {
            background: black;
          }
        `};
      }
    }
  `}
`)

export const DotsNavigation = memo(styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  li {
    line-height: 0;
    margin: 0 0 0.875rem;
    &:last-child {
      margin: 0;
    }
    button {
      position: relative;
      width: 1rem;
      height: 1rem;
      padding: 0;
      border-radius: 50%;
      background: ${(props) => props.theme.lightGreen};
      ${media.greaterThan('phone')`
        transition: background 300ms linear;
        will-change: background;
      `}
      span {
        display: block;
        top: 50%;
        right: calc(100% + 0.5rem);
        font-size: 0.625rem;
        line-height: 0.875rem;
        color: black;
        opacity: 0;
        transform: translateY(-50%);
        white-space: nowrap;
        text-transform: uppercase;
        position: absolute;
        z-index: -1;
        ${media.greaterThan('phone')`
          transition: all 300ms;
          transition-property: opacity;
          will-change: opacity;
        `}
        ${media.lessThan('phone')`
          display: none;
        `};
      }
      ${media.greaterThan('phone')`
        &:hover {
          background: ${(props) => props.theme.blue};
          span {
            opacity: 1;
          }
        }
      `}
      &.active {
        background: ${(props) => props.theme.darkBlue};
        ${media.greaterThan('phone')`
          &:hover {
            background: ${(props) => props.theme.darkBlue} !important;
          }
        `}
      }
    }
  }
  .quienes-somos &{
    li {
      button {
        span {
          color: white;
        }
        ${media.greaterThan('phone')`
          &:hover {
            background: ${(props) => props.theme.orange};
          }
        `}
      }
    }
  }
  .distinciones & {
    li {
      button {
        ${media.greaterThan('phone')`
          &:hover {
            background: ${(props) => props.theme.green};
          }
        `}
      }
    }
  }
  .contacto & {
    li {
      button {
        background: ${(props) => props.theme.whiteTransparent};
        ${media.lessThan('phone')`
          background: ${(props) => props.theme.lightGray};
        `};
        span {
          color: white;
        }
        ${media.greaterThan('phone')`
          &:hover {
            background: ${(props) => props.theme.green};
          }
        `}
        &.active {
          background: ${(props) => props.theme.blue};
        }
      }
    }
  }
`)

export const ShowNextButton = memo(styled(motion.button)`
  width: 3rem;
  height: 3rem;
  display: grid;
  place-items: center;
  z-index: 10;
  div {
    display: grid;
    place-items: center;
    width: 2.375rem;
    height: 2.375rem;
    background-color: ${(props) => props.theme.orange};
    border-radius: 50%;
    ${media.greaterThan('phone')`
      transition: all 300ms ease-out;
      transition-property: background-color, transform;
      will-change: background-color, transform;
    `}
  }
  svg {
    width: 60%;
    height: 60%;
    object-fit: contain;
    fill: white;
  }
  ${media.greaterThan('phone')`
    &:hover {
      div {
        transform: scale(1.1);
        background-color: ${(props) => props.theme.darkBlue};
      }
    }
  `}
`)

export const Menu = memo(styled(motion.nav)`
  position: relative;
  padding-left: 10%;
  z-index: 10;
  ${media.lessThan('tablet')`
    padding-left: 20%;
  `}
  ${media.lessThan('phone')`
    padding-left: 0;
  `};
`)

export const MenuLayer = memo(styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`)
